import { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import type { MessageQueue } from '../../types/messageQueue';
import {
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  FormCheckbox,
  Button,
  Badge,
} from '../ui';

interface MessageQueueFormProps {
  queue: MessageQueue | null;
  integrations: any[];
  allQueues?: MessageQueue[];
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function MessageQueueForm({
  queue,
  integrations,
  allQueues = [],
  onSubmit,
  isLoading,
  onClose,
}: MessageQueueFormProps) {
  const [subjects, setSubjects] = useState<string[]>(queue?.subjects || []);
  const [newSubject, setNewSubject] = useState('');

  useEffect(() => {
    if (queue) {
      setSubjects(queue.subjects || []);
    }
  }, [queue]);

  const handleAddSubject = () => {
    const trimmedSubject = newSubject.trim();
    if (trimmedSubject && !subjects.includes(trimmedSubject)) {
      setSubjects([...subjects, trimmedSubject]);
      setNewSubject('');
    }
  };

  const handleRemoveSubject = (subject: string) => {
    setSubjects(subjects.filter(s => s !== subject));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSubject();
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="name" required>
          Nome da Fila
        </FormLabel>
        <FormInput id="name" name="name" defaultValue={queue?.name} required />
      </FormField>

      <FormField>
        <FormLabel htmlFor="subjects">Assuntos</FormLabel>
        <div className="flex gap-2 mb-2">
          <FormInput
            type="text"
            value={newSubject}
            onChange={e => setNewSubject(e.target.value)}
            placeholder="Adicionar assunto"
            className="flex-1"
            onKeyPress={handleKeyPress}
          />
          <Button type="button" onClick={handleAddSubject} disabled={!newSubject.trim()} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {subjects.map((subject, index) => (
            <div
              key={index}
              className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full text-sm"
            >
              {subject}
              <button
                type="button"
                onClick={() => handleRemoveSubject(subject)}
                className="ml-1 hover:text-red-500 focus:outline-none"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>

        {/* Hidden inputs para envio do formulário */}
        {subjects.map((subject, index) => (
          <input
            key={`hidden-${index}`}
            type="hidden"
            name={`subjects[${index}]`}
            value={subject}
          />
        ))}
      </FormField>

      <FormField>
        <FormLabel htmlFor="integration_id">Integração</FormLabel>
        <FormSelect
          id="integration_id"
          name="integration_id"
          defaultValue={queue?.integration_id || ''}
        >
          <option value="">Selecione uma integração (opcional)</option>
          {integrations.map(integration => (
            <option key={integration.id} value={integration.id}>
              {integration.description}
            </option>
          ))}
        </FormSelect>
      </FormField>

      <FormField>
        <FormLabel htmlFor="integration_key">Chave de Integração</FormLabel>
        <FormInput
          id="integration_key"
          name="integration_key"
          type="text"
          defaultValue={queue?.integration_key || ''}
          placeholder="ID ou chave para integração externa"
        />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="timeout_queue_id">Fila de Timeout</FormLabel>
          <FormSelect
            id="timeout_queue_id"
            name="timeout_queue_id"
            defaultValue={queue?.timeout_queue_id || ''}
          >
            <option value="">Nenhuma</option>
            {allQueues
              .filter(q => q.id !== queue?.id)
              .map(q => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="timeout_queue">Tempo de Timeout da Fila (minutos)</FormLabel>
          <FormInput
            id="timeout_queue"
            name="timeout_queue"
            type="number"
            min="0"
            defaultValue={queue?.timeout_queue ?? 20}
            placeholder="20"
          />
        </FormField>
      </div>

      <FormField>
        <FormLabel htmlFor="pabx_refer">Referência do PABX</FormLabel>
        <FormInput
          id="pabx_refer"
          name="pabx_refer"
          type="text"
          defaultValue={queue?.pabx_refer || ''}
          placeholder="Referência do PABX associado à fila (opcional)"
        />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <FormField>
          <div className="flex items-center">
            <FormCheckbox id="default" name="default" defaultChecked={queue?.default || false} />
            <FormLabel htmlFor="default" className="ml-2 mb-0">
              Fila Padrão
            </FormLabel>
          </div>
        </FormField>

        <FormField>
          <div className="flex items-center">
            <FormCheckbox
              id="screening"
              name="screening"
              defaultChecked={queue?.screening || false}
            />
            <FormLabel htmlFor="screening" className="ml-2 mb-0">
              Requer Triagem
            </FormLabel>
          </div>
        </FormField>

        <FormField>
          <div className="flex items-center">
            <FormCheckbox
              id="show_user"
              name="show_user"
              defaultChecked={queue?.show_user !== false}
            />
            <FormLabel htmlFor="show_user" className="ml-2 mb-0">
              Exibir para Usuário
            </FormLabel>
          </div>
        </FormField>

        <FormField>
          <div className="flex items-center">
            <FormCheckbox
              id="ia_respond"
              name="ia_respond"
              defaultChecked={queue?.ia_respond || false}
            />
            <FormLabel htmlFor="ia_respond" className="ml-2 mb-0">
              IA Responde Automaticamente
            </FormLabel>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Se marcado, a IA responderá automaticamente às mensagens nesta fila
          </p>
        </FormField>

        <FormField>
          <div className="flex items-center">
            <FormCheckbox
              id="timeout_messages"
              name="timeout_messages"
              defaultChecked={queue?.timeout_messages || false}
            />
            <FormLabel htmlFor="timeout_messages" className="ml-2 mb-0">
              Mensagens de Timeout
            </FormLabel>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Se marcado, mensagens de timeout estarão habilitadas
          </p>
        </FormField>
      </div>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {queue ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default MessageQueueForm;
